import React, { useContext, useEffect } from "react";
import { themeContext } from "../Context";
import { toPng } from "html-to-image";
import { saveAs } from "file-saver";

const ExplanationPopup = ({ question, onClose, language: parentLanguage }) => {
    const theme = useContext(themeContext);
    const darkMode = theme.state.darkMode;

    // Use parent language if provided, otherwise default to 'en'
    const language = parentLanguage || 'en';

    // Get Hindi explanation if available
    const getLocalizedExplanation = () => {
        if (!question) return '';

        const hindiExplanation = question.explanationHi; // Assuming you have Hindi explanation field

        if (language === 'en') {
            return question.explanation || 'No explanation available.';
        }

        if (language === 'hi' && hindiExplanation) {
            return hindiExplanation;
        }

        if (language === 'bilingual' && hindiExplanation) {
            return `${question.explanation}\n\n${hindiExplanation}`;
        }

        // Fallback to English
        return question.explanation || 'No explanation available.';
    };

    // Get language display name for UI
    const getLanguageDisplay = () => {
        switch (language) {
            case 'hi': return 'हिंदी';
            case 'bilingual': return 'Bilingual';
            default: return 'English';
        }
    };

    // Prevent scrolling on the background
    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    const handleDownload = () => {
        const element = document.getElementById(`explanation-${question.id}`);
        if (!element) {
            console.error("Element not found");
            return;
        }

        const { width, height } = element.getBoundingClientRect();
        const aspectRatio = width / height;
        const canvasWidth = 400;
        const canvasHeight = canvasWidth / aspectRatio;

        toPng(element, {
            quality: 2,
            canvasWidth,
            canvasHeight,
        })
            .then((dataUrl) => {
                saveAs(dataUrl, `explanation-${question.id}.png`);
            })
            .catch((err) => console.error("Error generating image:", err));
    };

    return (
        <>
            {/* Overlay with blur effect */}
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "rgba(0, 0, 0, 0.6)",
                    backdropFilter: "blur(8px)",
                    zIndex: 999,
                    animation: "fadeIn 0.3s ease"
                }}
                onClick={onClose}
            ></div>

            {/* Explanation Popup Modal */}
            <div
                style={{
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1000,
                    background: darkMode ? "#2d2d2d" : "#ffffff",
                    color: darkMode ? "#ffffff" : "#333",
                    padding: window.innerWidth <= 768 ? "16px" : "24px",
                    borderRadius: "24px",
                    boxShadow: darkMode
                        ? "0 20px 40px rgba(0, 0, 0, 0.5)"
                        : "0 20px 40px rgba(0, 0, 0, 0.15)",
                    maxWidth: "90%",
                    width: "700px",
                    maxHeight: "90%",
                    overflowY: "auto",
                    animation: "slideUp 0.3s ease"
                }}
            >
                {/* Header with language indicator */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    paddingBottom: "15px",
                    borderBottom: darkMode ? "1px solid #444" : "1px solid #e0e0e0"
                }}>
                    <h3 style={{
                        fontSize: window.innerWidth <= 768 ? "18px" : "20px",
                        fontWeight: "600",
                        color: darkMode ? "#ffffff" : "#333",
                        margin: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                    }}>
                        📝 Explanation
                        {question.explanationHi && (
                            <span style={{
                                fontSize: "12px",
                                backgroundColor: "#28a745",
                                color: "white",
                                padding: "4px 10px",
                                borderRadius: "20px",
                                fontWeight: "normal"
                            }}>
                                {getLanguageDisplay()}
                            </span>
                        )}
                    </h3>
                </div>

                {/* Main content container for download */}
                <div
                    id={`explanation-${question.id}`}
                    style={{
                        background: darkMode ? "#2d2d2d" : "#ffffff",
                        borderRadius: "20px",
                        overflow: "hidden",
                        border: darkMode ? "1px solid #444" : "1px solid #e0e0e0",
                    }}
                >
                    {/* Question section */}
                    <div style={{
                        padding: "20px",
                        background: darkMode ? "#363636" : "#f8f9fa",
                        borderBottom: darkMode ? "1px solid #444" : "1px solid #e0e0e0"
                    }}>
                        <p style={{
                            fontSize: window.innerWidth <= 768 ? "14px" : "15px",
                            fontWeight: "500",
                            color: darkMode ? "#ffffff" : "#333",
                            lineHeight: "1.4",
                            margin: 0,
                            whiteSpace: "pre-line"
                        }}>
                            <span style={{ color: '#007bff', marginRight: '8px' }}>Q:</span>
                            {question.question}
                        </p>
                    </div>

                    {/* Image section if exists */}
                    {question.image && (
                        <div style={{
                            padding: "20px",
                            textAlign: "center",
                            background: darkMode ? "#2d2d2d" : "#ffffff",
                            borderBottom: darkMode ? "1px solid #444" : "1px solid #e0e0e0"
                        }}>
                            <img
                                src={question.image}
                                alt="Explanation visual"
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "250px",
                                    borderRadius: "12px",
                                    boxShadow: darkMode
                                        ? "0 4px 12px rgba(0,0,0,0.3)"
                                        : "0 4px 12px rgba(0,0,0,0.1)",
                                    border: darkMode
                                        ? "1px solid #444"
                                        : "1px solid #e0e0e0",
                                    objectFit: "contain",
                                    backgroundColor: darkMode ? "#363636" : "#f8f9fa",
                                    padding: "4px"
                                }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                }}
                            />
                        </div>
                    )}

                    {/* Explanation section */}
                    <div style={{
                        padding: "24px",
                        background: darkMode ? "#2d2d2d" : "#ffffff"
                    }}>
                        <div style={{
                            background: darkMode ? "#363636" : "#f8f9fa",
                            borderRadius: "16px",
                            padding: "20px",
                            border: darkMode ? "1px solid #444" : "1px solid #e0e0e0"
                        }}>
                            <p style={{
                                fontSize: window.innerWidth <= 768 ? "12px" : "14px",
                                lineHeight: "1.8",
                                color: darkMode ? "#e0e0e0" : "#444",
                                margin: 0,
                                whiteSpace: "pre-line"
                            }}>
                                {getLocalizedExplanation()}
                            </p>
                        </div>
                    </div>

                    {/* Footer with copyright */}
                    <div style={{
                        padding: "15px 20px",
                        background: darkMode ? "#363636" : "#f8f9fa",
                        borderTop: darkMode ? "1px solid #444" : "1px solid #e0e0e0",
                        textAlign: "center"
                    }}>
                        <p style={{
                            fontSize: "13px",
                            color: darkMode ? "#999" : "#666",
                            margin: 0
                        }}>
                            © SSCVIDYA.IN
                        </p>
                    </div>
                </div>

                {/* Action buttons */}
                <div style={{
                    display: "flex",
                    gap: "10px",
                    marginTop: "20px",
                    justifyContent: "flex-end"
                }}>
                    <button
                        onClick={handleDownload}
                        style={{
                            padding: window.innerWidth <= 768 ? '10px 20px' : '12px 24px',
                            borderRadius: '30px',
                            border: 'none',
                            backgroundColor: '#28a745',
                            color: 'white',
                            fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 4px 12px rgba(40,167,69,0.3)'
                        }}
                    >
                        📥 Download
                    </button>
                    <button
                        onClick={onClose}
                        style={{
                            padding: window.innerWidth <= 768 ? '10px 20px' : '12px 24px',
                            borderRadius: '30px',
                            border: 'none',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 4px 12px rgba(108,117,125,0.3)'
                        }}
                    >
                        ✕ Close
                    </button>
                </div>
            </div>

            {/* Add animation styles */}
            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translate(-50%, -40%);
                    }
                    to {
                        opacity: 1;
                        transform: translate(-50%, -50%);
                    }
                }
            `}</style>
        </>
    );
};

export default ExplanationPopup;

//OLDER VERSION
// import React, { useContext } from "react";
// import { themeContext } from "../Context";
// import { toPng } from "html-to-image";
// import { saveAs } from "file-saver";

// const ExplanationPopup = ({ question, onClose }) => {
//     const theme = useContext(themeContext);
//     const darkMode = theme.state.darkMode;

//     // Prevent scrolling on the background
//     React.useEffect(() => {
//         document.body.style.overflow = "hidden";
//         return () => {
//             document.body.style.overflow = "auto";
//         };
//     }, []);

//     // const truncateText = (text, maxLength = 200) => {
//     //     if (text.length <= maxLength) return text;
//     //     return text.substring(0, maxLength) + "...";
//     // };

//     const handleDownload = () => {
//         const element = document.getElementById(`explanation-${question.id}`);
//         if (!element) {
//             console.error("Element not found");
//             return;
//         }

//         const { width, height } = element.getBoundingClientRect(); // Get element's current dimensions
//         const aspectRatio = width / height;

//         // Adjust canvas size proportionally
//         const canvasWidth = 300; // Example base width
//         const canvasHeight = canvasWidth / aspectRatio;

//         toPng(element, {
//             quality: 2,
//             canvasWidth,
//             canvasHeight,
//         })
//             .then((dataUrl) => {
//                 saveAs(dataUrl, `question-${question.id}.png`);
//             })
//             .catch((err) => console.error("Error generating image:", err));
//     };


//     return (
//         <>
//             {/* Overlay for background blur and disabling interaction */}
//             <div
//                 style={{
//                     position: "fixed",
//                     top: 0,
//                     left: 0,
//                     width: "100%",
//                     height: "100%",
//                     background: "rgba(0, 0, 0, 0.5)",
//                     backdropFilter: "blur(5px)",
//                     zIndex: 999,
//                 }}
//             ></div>

//             {/* Explanation Popup Modal */}
//             <div
//                 style={{
//                     position: "fixed",
//                     top: "50%",
//                     left: "50%",
//                     transform: "translate(-50%, -50%)",
//                     zIndex: 1000,
//                     background: darkMode ? "#222" : "white",
//                     color: darkMode ? "white" : "black",
//                     padding: "20px",
//                     borderRadius: "10px",
//                     boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
//                     maxWidth: "90%",
//                     width: "600px",
//                     maxHeight: "90%",
//                     overflowY: "auto",
//                 }}
//             >
//                 {/* <h3>Explanation</h3> */}
//                 {/* Image Display (if exists) */}
//                 <div style={{
//                     padding: "20px",
//                     borderRadius: "10px",
//                     border: darkMode
//                         ? "1px solid #444"
//                         : "1px solid #e0e0e0",
//                     textAlign: "center",
//                     background: darkMode ? "#222" : "white",
//                 }}
//                     id={`explanation-${question.id}`

//                     }>
//                     {question.image && (
//                         <div style={{
//                             margin: "15px 0",
//                             textAlign: "center",
//                             display: "flex",
//                             justifyContent: "center"
//                         }}>
//                             <img
//                                 src={question.image}
//                                 alt="Explanation visual"
//                                 style={{
//                                     maxWidth: "100%",
//                                     maxHeight: "300px",
//                                     borderRadius: "8px",
//                                     boxShadow: darkMode
//                                         ? "0 4px 8px rgba(0,0,0,0.3)"
//                                         : "0 2px 6px rgba(0,0,0,0.1)",
//                                     border: darkMode
//                                         ? "1px solid #444"
//                                         : "1px solid #e0e0e0",
//                                     objectFit: "contain",
//                                     backgroundColor: darkMode
//                                         ? "#2a2a2a"
//                                         : "#f9f9f9",
//                                     padding: "4px"
//                                 }}
//                                 onError={(e) => {
//                                     e.target.style.display = 'none'; // Hide if image fails to load
//                                 }}
//                             />
//                         </div>
//                     )}
//                     <footer
//                         style={{
//                             position: "relative",
//                             bottom: "7px",
//                             background: darkMode ? "#555" : "#f1f1f1",
//                             color: darkMode ? "white" : "black",
//                             padding: "5px 10px",
//                             borderRadius: "5px",
//                         }}
//                     >
//                         <p style={{ fontSize: "14px", margin: 0 }}>© SSCVIDYA.IN</p>
//                     </footer>
//                     <div
//                         style={{
//                             background: darkMode ? "#333" : "white",
//                             color: darkMode ? "white" : "black",
//                             border: darkMode ? "2px solid #555" : "2px solid #000",
//                             borderRadius: "10px",
//                             padding: "20px",
//                             margin: "0 auto",
//                             textAlign: "center",
//                         }}
//                     >
//                         <div style={{
//                             marginBottom: "20px",
//                             border: darkMode ? "1px solid #555" : "1px solid #000",
//                             borderRadius: "10px",
//                             padding: "20px",
//                             textAlign: "left"
//                         }}>
//                             {/* <h2 style={{ fontSize: "20px", marginBottom: "10px" }}>{truncateText(question.question, 200)}</h2> */}
//                             {/* {question.question} */}
//                             <p style={{ fontSize: "16px", lineHeight: "1.6", whiteSpace: "pre-line" }}>
//                                 {question.explanation}
//                             </p>
//                         </div>
//                     </div>

//                 </div>
//                 <button
//                     onClick={handleDownload}
//                     className="button"
//                     style={{ marginTop: "10px", marginRight: "10px" }}
//                 >
//                     Download
//                 </button>
//                 <button
//                     onClick={onClose}
//                     className="button"
//                     style={{ marginTop: "10px" }}
//                 >
//                     Close
//                 </button>
//             </div>
//         </>
//     );
// };

// export default ExplanationPopup;

